import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import { OverflowTooltip, Table } from "@4uhub/lib4uhub";
import MainApi from "../../../../../../services/mainApi.service";
import { useCallback, useMemo, useState } from "react";
import { ITicketAutomationReceivedRuleCancelationReason } from "../../../../../../models/ticket-automation-received-rule-cancelation-reason";
import CancelationReasons from "./CancelationReasonsForm/CancelationReasons";

const TICKET_AUTOMATION_RECEIVED_RULE_CANCELATION_REASON_ROUTE =
  process.env.REACT_APP_AUTOMATIONS +
  "/api/v1/TicketAutomationReceivedRuleCancelationReason/";
const ticketAutomationReceivedRuleCancelationReasonService =
  new MainApi<ITicketAutomationReceivedRuleCancelationReason>(
    TICKET_AUTOMATION_RECEIVED_RULE_CANCELATION_REASON_ROUTE,
  );

const translationPath =
  "chatBot.page.automations.automationReceivedRules.automationReceivedRuleCancelationReason.";

interface ICancelationReasonsPageProps {
  automationCode?: string;
}

const CancelationReasonsPage = ({
  automationCode,
}: ICancelationReasonsPageProps) => {
  const { t } = useTranslation();

  const { id: ticketAutomationReceivedRuleId } = useParams();

  const [cancelationReasonForm, setCancelationReasonForm] = useState(false);

  const [selectedId, setSelectedId] = useState<string>();

  const columns: GridColDef<ITicketAutomationReceivedRuleCancelationReason>[] =
    [
      {
        field: "externalId",
        headerName: t(translationPath + "external_id") || "External ID",
      },
      {
        field: "externalName",
        headerName: t(translationPath + "external_name") || "External Name",
        renderCell: (params) =>
          params.value ? (
            <OverflowTooltip
              text={params.value}
              tooltip={params.value}
              textTransform="none"
              variant="body2"
            />
          ) : (
            "-"
          ),
      },
    ];

  const handleAdd = () => {
    setCancelationReasonForm(true);
  };

  const handleRowClick = (
    params: GridRowParams<ITicketAutomationReceivedRuleCancelationReason>,
  ) => {
    if (!params.row) return;
    setCancelationReasonForm(true);
    setSelectedId(params.row.id);
  };

  const handleBack = useCallback(() => {
    setSelectedId(undefined);
    setCancelationReasonForm(false);
  }, []);

  const props = useMemo(() => {
    return {
      TicketAutomationReceivedRuleId: ticketAutomationReceivedRuleId,
    };
  }, [ticketAutomationReceivedRuleId]);

  return (
    <Box sx={{ height: 600, width: "100%" }}>
      {cancelationReasonForm && (
        <Box
          sx={(t) => ({
            p: 1,
            px: 10,
            width: "100%",
            height: "100%",
            overflow: "auto",
            backgroundColor: t.palette.background.default,
            borderRadius: 1,
          })}
        >
          <CancelationReasons
            id={selectedId}
            ticketAutomationReceivedRuleId={ticketAutomationReceivedRuleId}
            automationCode={automationCode}
            onBackButton={handleBack}
          />
        </Box>
      )}
      {!cancelationReasonForm && (
        <Box height={600}>
          <Table<ITicketAutomationReceivedRuleCancelationReason>
            showDefaultMacroFilters={false}
            service={ticketAutomationReceivedRuleCancelationReasonService}
            columns={columns}
            title={""}
            searchInput
            onAdd={handleAdd}
            onRowClick={handleRowClick}
            searchInputAutoFocus={false}
            defaultPageSize={20}
            serviceProps={props}
            pageSizeOptions={[5, 10, 20]}
            searchInputPlaceHolder={""}
            addButtonLabel={
              t(translationPath + "add_cancelation_reason") ||
              "Add Cancelation Reason"
            }
            confirmOptions={{
              title: t(translationPath + "confirm.title"),
              description: t(translationPath + "confirm.description"),
              confirmationButtonProps: {
                variant: "contained",
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default CancelationReasonsPage;
